import React from 'react';

import { useNotificationSubscriptionAuthed } from 'App/DetailsPage/hooks/subscription.hooks';
import { extLnkDeliveryPref, myPostBaseUrl } from 'config/track-config';
import { trackInteraction } from 'store/analytics/analytics.thunks';
import { selectIsAuthed } from 'store/customer/customer.selectors';
import { clearNotification } from 'store/notification';
import { useAppDispatch, useAppSelector } from 'store/store';
import { reportNewRelicError } from 'utils/window.utils';

import { authService } from '../../../../store/customer/utils/auth0.util';
import { loginAndReturn } from '../../../../utils/login.utils';
import { useCurrentArticle } from '../../hooks/shipment.hooks';
import AccordionWrapper from '../AccordionWrapper/AccordionWrapper';
import ButtonRow from '../RowCard/ButtonRow/ButtonRow';
import LinkRow from '../RowCard/LinkRow/LinkRow';
import RowCard from '../RowCard/RowCard';
import { ROW_CARD_ICONS } from '../RowCard/RowContent/RowContent';
import ToggleRow from '../RowCard/ToggleRow/ToggleRow';
import {
  NOTIFICATION_OPTIONS_TEST_ID,
  NOTIFICATIONS_PREFERENCES_ROW_TEST_ID,
  NOTIFICATIONS_SUBSCRIPTION_ROW_TEST_ID,
} from './NotificationOptions.test-ids';

const NotificationOptions: React.FC = () => {
  const isAuthed = useAppSelector(selectIsAuthed) || false;
  const trackingNumber = useCurrentArticle()?.articleId || '';
  const heading = 'Notifications';

  const { enableNotificationSubscription, disableNotificationSubscription, loading } =
    useNotificationSubscriptionAuthed();

  const notificationOptions = (
    <>
      {notificationSubscriptionRow(isAuthed, enableNotificationSubscription, disableNotificationSubscription, loading)}
      {managePreferencesRow(isAuthed, trackingNumber)}
    </>
  );

  return (
    <AccordionWrapper heading={heading} id='accordionNotificationOptions'>
      <RowCard groupHeading={heading} data-testid={NOTIFICATION_OPTIONS_TEST_ID}>
        {notificationOptions}
      </RowCard>
    </AccordionWrapper>
  );
};

const notificationSubscriptionRow = (
  isAuthed: boolean,
  enableNotificationSubscription: () => Promise<boolean>,
  disableNotificationSubscription: () => Promise<boolean>,
  loading: boolean
) => {
  const dispatch = useAppDispatch();
  const hasNotification = !!useCurrentArticle()?.hasNotification;
  const [notificationOn, setIsNotificationOn] = React.useState(false);

  React.useEffect(() => {
    setIsNotificationOn(hasNotification);
  }, [hasNotification]);

  if (!isAuthed) return null;

  const handleNotificationSubscriptionClick = () => {
    dispatch(clearNotification());
    if (loading) return;

    if (notificationOn) {
      setIsNotificationOn(false);
      disableNotificationSubscription()
        .then(setIsNotificationOn)
        .catch((err) => reportNewRelicError(err));
    } else {
      setIsNotificationOn(true);
      enableNotificationSubscription()
        .then(setIsNotificationOn)
        .catch((err) => reportNewRelicError(err));
    }

    dispatch(trackInteraction(`notifications preference:${notificationOn ? 'off' : 'on'}`));
  };

  return (
    <ToggleRow
      icon={ROW_CARD_ICONS.notification}
      text='For this parcel'
      isToggled={notificationOn}
      onClick={handleNotificationSubscriptionClick}
      data-testid={NOTIFICATIONS_SUBSCRIPTION_ROW_TEST_ID}
    />
  );
};

const managePreferencesRow = (isAuthed: boolean, trackingNumber: string | undefined) => {
  const notificationPrefLink =
    extLnkDeliveryPref + '/?entry=TRACK&next=' + encodeURIComponent('/mypost/track/details/' + trackingNumber);

  if (!isAuthed)
    return (
      <ButtonRow
        icon={ROW_CARD_ICONS.notificationSettings}
        text='Set preferences'
        data-testid={NOTIFICATIONS_PREFERENCES_ROW_TEST_ID}
        onClick={async () => {
          await authService?.logout(myPostBaseUrl + '/track/', true);
          await loginAndReturn(window.location.href);
        }}
      />
    );

  return (
    <LinkRow
      icon={ROW_CARD_ICONS.notificationSettings}
      text='Manage preferences'
      href={notificationPrefLink}
      target='_blank'
      data-testid={NOTIFICATIONS_PREFERENCES_ROW_TEST_ID}
    />
  );
};

export default NotificationOptions;
